import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiEye,
  FiEdit3,
  FiTrash2,
  FiUser,
  FiCalendar,
  FiClock,
  FiAlertTriangle,
  FiInfo,
  FiCheck
} from 'react-icons/fi';

const AppointmentTreatmentTable = ({ 
  appointments = [], 
  onViewAppointment,
  onEditAppointment,
  onDeleteAppointment,
  onReloadAppointments,
  isLoading = false,
  globalFilter,
  onGlobalFilterChange,
  patientData
}) => {
  
  const [rowSelection, setRowSelection] = useState({});
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  // Function to determine status color and icon
  const getStatusIndicator = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return { color: '#6B7280', dot: '●' }; // Gray
      case 'confirmed':
        return { color: '#10B981', dot: '●' }; // Green
      case 'waiting':
        return { color: '#F59E0B', dot: '●' }; // Amber
      case 'called':
        return { color: '#F97316', dot: '●' }; // Orange
      case 'in consultation':
        return { color: '#3B82F6', dot: '●' }; // Blue
      case 'completed':
        return { color: '#059669', dot: '●' }; // Emerald
      case 'cancelled':
        return { color: '#EF4444', dot: '●' }; // Red
      case 'no show':
        return { color: '#DC2626', dot: '●' }; // Dark Red
      default:
        return { color: '#9CA3AF', dot: '●' }; // Light Gray
    }
  };

  // Function to get priority color and icon
  const getPriorityIndicator = (priority, isUrgent) => {
    if (isUrgent) {
      return { color: '#DC2626', icon: <FiAlertTriangle />, text: 'URGENT' };
    }
    
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return { color: '#DC2626', icon: <FiAlertTriangle />, text: 'Urgent' };
      case 'high':
        return { color: '#F97316', icon: <FiInfo />, text: 'High' };
      case 'normal':
        return { color: '#10B981', icon: <FiCheck />, text: 'Normal' };
      case 'low':
        return { color: '#6B7280', icon: <FiInfo />, text: 'Low' };
      default:
        return { color: '#6B7280', icon: <FiInfo />, text: 'Normal' };
    }
  };

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Function to format time
  const formatTime = (timeString) => {
    if (!timeString) return 'No time';
    return timeString;
  };

  // Function to highlight search text
  const highlightText = (text, searchTerm) => {
    if (!text || !searchTerm) return text || '';
    
    const textStr = String(text);
    const searchStr = String(searchTerm).toLowerCase();
    
    if (!textStr.toLowerCase().includes(searchStr)) {
      return textStr;
    }
    
    try {
      const parts = textStr.split(new RegExp(`(${searchTerm})`, 'gi'));
      return (
        <>
          {parts.map((part, index) => 
            part.toLowerCase() === searchStr.toLowerCase() ? 
              <span key={index} style={{ backgroundColor: '#FFFF00', fontWeight: 'bold' }}>{part}</span> : 
              part
          )}
        </>
      );
    } catch (error) {
      return textStr;
    }
  };

  // Column definitions
  const columns = useMemo(() => [
    {
      accessorKey: 'appointmentId',
      header: 'Appointment ID',
      size: 100,
      Cell: ({ cell }) => (
        <div className="fw-medium text-primary">
          {highlightText(cell.getValue() || 'N/A', globalFilter)}
        </div>
      ),
    },
    {
      accessorKey: 'appointment_date',
      header: 'Date & Time',
      size: 150,
      Cell: ({ cell, row }) => (
    <div>
      <div className="fw-medium">
        <FiCalendar className="me-1" size={14} />
        {formatDate(cell.getValue())}
      </div>
      <small className="text-muted">
        <FiClock className="me-1" size={12} />
        {formatTime(row.original.appointment_time)}
        {row.original.end_time && ` - ${formatTime(row.original.end_time)}`}
      </small>
    </div>
  ),
    },
    {
  accessorKey: 'doctor_name',
  header: 'Doctor',
  size: 180,
  Cell: ({ cell, row }) => (
    <div>
      <div className="fw-medium">
        <FiUser className="me-1" size={14} />
        {highlightText(cell.getValue() || 'Not assigned', globalFilter)}
      </div>
      <small className="text-muted">
        {row.original.doctor_specialization || 'No specialty'}
      </small>
    </div>
  ),
},
    {
      accessorKey: 'appointment_type',
      header: 'Type',
      size: 120,
      Cell: ({ cell }) => (
        <span className="badge bg-info">
          {highlightText(cell.getValue() || 'Not specified', globalFilter)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        const statusIndicator = getStatusIndicator(status);
        
        return (
          <div className="d-flex align-items-center gap-1">
            <span style={{ color: statusIndicator.color, fontSize: '16px' }}>
              {statusIndicator.dot}
            </span>
            <span style={{ color: statusIndicator.color, fontSize: '0.875rem' }}>
              {highlightText(status || 'Unknown', globalFilter)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 100,
      Cell: ({ row }) => {
        const priority = row.original.priority;
        const isUrgent = row.original.is_urgent;
        const priorityIndicator = getPriorityIndicator(priority, isUrgent);
        
        return (
          <div className="d-flex align-items-center gap-1">
            <span style={{ color: priorityIndicator.color }}>
              {priorityIndicator.icon}
            </span>
            <span style={{ color: priorityIndicator.color, fontSize: '0.875rem' }}>
              {priorityIndicator.text}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'reason_for_visit',
      header: 'Reason',
      size: 200,
      Cell: ({ cell }) => (
        <div className="text-wrap">
          {highlightText(cell.getValue() || 'Not specified', globalFilter)}
        </div>
      ),
    },
    {
      accessorKey: 'room_number',
      header: 'Room',
      size: 80,
      Cell: ({ cell }) => (
        <span className="badge bg-secondary">
          {cell.getValue() || 'TBD'}
        </span>
      ),
    },
    {
      accessorKey: 'consultation_status',
      header: 'Consultation',
      size: 120,
      Cell: ({ row }) => {
        const checkedIn = row.original.checked_in;
        const consultationStarted = row.original.consultation_started;
        const consultationCompleted = row.original.consultation_completed;
        
        let status = 'Not Started';
        let color = 'secondary';
        
        if (consultationCompleted) {
          status = 'Completed';
          color = 'success';
        } else if (consultationStarted) {
          status = 'In Progress';
          color = 'primary';
        } else if (checkedIn) {
          status = 'Checked In';
          color = 'info';
        }
        
        return (
          <span className={`badge bg-${color}`}>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      size: 90,
      Cell: ({ cell }) => (
        <small className="text-muted">
          {cell.getValue() || 30} min
        </small>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewAppointment && onViewAppointment(row.original.appointment_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onEditAppointment && onEditAppointment(row.original.appointment_id);
            }}
            title="Edit"
          >
            <FiEdit3 />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteAppointment && onDeleteAppointment(row.original.appointment_id);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ], [globalFilter, onViewAppointment, onEditAppointment, onDeleteAppointment]);

  // Handle global filter change
  const handleGlobalFilterChange = (filter) => {
    onGlobalFilterChange && onGlobalFilterChange(filter);
  };

  // Handle offcanvas close
const handleCloseOffcanvas = () => {
  setShowOffcanvas(false);
  setSelectedAppointment(null);
};

// Handle actions from offcanvas
const handleOffcanvasEdit = () => {
  handleCloseOffcanvas();
  onEditAppointment && onEditAppointment(selectedAppointment.appointment_id);
};

const handleOffcanvasDelete = () => {
  handleCloseOffcanvas();
  onDeleteAppointment && onDeleteAppointment(selectedAppointment.appointment_id);
};

    // Memoized and normalized appointments data
// Reemplaza la función memoizedAppointments en AppointmentTreatmentTable.js líneas ~226-280

const memoizedAppointments = useMemo(() => {
  console.log('🎯 Procesando datos de appointments:', appointments);
  
  return appointments.map((appointment, index) => {
    // Log para debug
    if (index < 2) {
      console.log(`📋 Appointment ${index} original data:`, appointment);
    }
    
    const mapped = {
      ...appointment,
      
      // ✅ IDs - usando los campos correctos de la API
      appointment_id: appointment._id || appointment.appointment_id || appointment.id,
      appointmentId: appointment.appointmentId || 'N/A',
      
      // ✅ FECHAS Y HORARIOS - campos correctos según la API
      appointment_date: appointment.date, // La API usa 'date'
      appointment_time: appointment.startTime || 'Not set', // La API usa 'startTime'
      end_time: appointment.endTime, // La API usa 'endTime'
      
      // ✅ INFORMACIÓN DEL DOCTOR - campos directos de la API
      doctor_name: appointment.doctorName || 'Not assigned', // La API tiene 'doctorName'
      doctor_specialization: appointment.doctorRole || 'No specialty', // La API tiene 'doctorRole'
      
      // ✅ INFORMACIÓN DE LA CITA
      appointment_type: appointment.appointmentType || 'Not specified', // La API usa 'appointmentType'
      status: appointment.status || 'pending',
      priority: appointment.priority || 'normal',
      is_urgent: appointment.isUrgent || false, // La API usa 'isUrgent'
      reason_for_visit: appointment.notes || 'Not specified', // La API usa 'notes'
      room_number: appointment.room_number || 'TBD',
      
      // ✅ ESTADOS DE CONSULTA
      checked_in: appointment.checkedIn || false,
      consultation_started: appointment.consultationStartTime ? true : false,
      consultation_completed: appointment.status === 'Completed',
      duration: appointment.duration || 30,
      
      // ✅ INFORMACIÓN DEL PACIENTE - campos directos de la API
      patient_name: appointment.patientName || 'Unknown patient', // La API tiene 'patientName'
      patient_avatar: appointment.patientAvatar || '',
      date_of_birth: appointment.dateOfBirth || '',
      address: appointment.address || '',
      
      // ✅ TIEMPOS Y GESTIÓN
      wait_time: appointment.waitTime || 0,
      consultation_duration: appointment.consultationDuration || 0,
      is_accepted: appointment.isaccepted || false,
      is_walk_in: appointment.isWalkIn || false,
      walk_in_time: appointment.walkInTime,
      rescheduled_count: appointment.rescheduledCount || 0,
      
      // ✅ TIMESTAMPS
      created_date: appointment.createdAt,
      updated_date: appointment.updatedAt,
      confirmed_at: appointment.confirmedAt,
      cancelled_at: appointment.cancelledAt,
      cancellation_reason: appointment.cancellationReason,
      
      // ✅ TIEMPOS DE CONSULTA
      consultation_start_time: appointment.consultationStartTime,
      consultation_end_time: appointment.consultationEndTime
    };
    
    // Debug para verificar el mapeo
    if (index < 2) {
      console.log(`✅ Mapped appointment ${index}:`, {
        id: mapped.appointment_id,
        appointmentId: mapped.appointmentId,
        doctorName: mapped.doctor_name,
        doctorRole: mapped.doctor_specialization,
        patientName: mapped.patient_name,
        status: mapped.status,
        date: mapped.appointment_date,
        time: mapped.appointment_time,
        type: mapped.appointment_type
      });
    }
    
    return mapped;
  });
}, [appointments]);

  // Empty state component
  const EmptyState = () => (
    <div className="text-center py-5">
      <FiCalendar size={48} className="text-muted mb-3" />
      <h5 className="text-muted">No Appointments Found</h5>
      <p className="text-muted">
        {patientData?.patientName 
          ? `No appointments found for ${patientData.patientName}.` 
          : 'No appointments available to display.'
        }
      </p>
      <button 
        className="btn btn-primary"
        onClick={() => onReloadAppointments && onReloadAppointments()}
      >
        Refresh Data
      </button>
    </div>
  );

  // If no appointments and not loading, show empty state
  if (!isLoading && (!memoizedAppointments || memoizedAppointments.length === 0)) {
    return <EmptyState />;
  }

  return (
    <div className="appointment-treatment-table">
      <MaterialReactTable
        columns={columns}
        data={memoizedAppointments}
        enableRowSelection
        enableMultiRowSelection
        enableSelectAll
        enableGlobalFilter
        globalFilterValue={globalFilter}
        onGlobalFilterChange={handleGlobalFilterChange}
        enableColumnFilters
        enableSorting
        enablePagination
        enableBottomToolbar
        enableTopToolbar
        muiTableContainerProps={{
          sx: { 
            maxHeight: '600px',
            minHeight: '400px'
          }
        }}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          }
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            padding: '12px 8px',
            fontSize: '0.875rem',
            borderBottom: '2px solid #dee2e6'
          }
        }}
        muiTableBodyCellProps={{
          sx: {
            padding: '8px 8px',
            fontSize: '0.875rem',
            borderBottom: '1px solid #e9ecef'
          }
        }}
        muiTableBodyRowProps={({ row }) => ({
  sx: {
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    cursor: 'pointer'
  },
  onClick: (e) => {
    // Evitar que se abra si se hace click en los botones de acciones
    if (e.target.closest('.btn')) {
      return;
    }
    console.log('Row clicked:', row.original);
    setSelectedAppointment(row.original);
    setShowOffcanvas(true);
  }
})}
        state={{
          rowSelection,
          isLoading,
          globalFilter: globalFilter || '',
        }}
        onRowSelectionChange={setRowSelection}
        initialState={{
          pagination: {
            pageSize: 10,
            pageIndex: 0,
          },
          density: 'comfortable',
          sorting: [
            {
              id: 'appointment_date',
              desc: true, // Most recent first
            },
          ],
        }}
        // Customize loading state
        muiLinearProgressProps={{
          color: 'primary',
        }}
        // Customize no data message
        renderEmptyRowsFallback={() => <EmptyState />}
        // Customize toolbar actions
        renderToolbarInternalActions={({ table }) => (
          <div className="d-flex align-items-center gap-2">
            {/* Refresh button */}
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onReloadAppointments && onReloadAppointments()}
              title="Refresh appointments"
            >
              <FiInfo />
            </button>
            
            {/* Selection info */}
            {Object.keys(rowSelection).length > 0 && (
              <span className="badge bg-primary">
                {Object.keys(rowSelection).length} selected
              </span>
            )}
          </div>
        )}
      />
      
      {/* Summary info */}
      {memoizedAppointments.length > 0 && (
        <div className="mt-3 d-flex justify-content-between align-items-center">
          <small className="text-muted">
            Total: {memoizedAppointments.length} appointment{memoizedAppointments.length !== 1 ? 's' : ''}
            {patientData?.patientName && ` for ${patientData.patientName}`}
          </small>
          
          {/* Quick stats */}
          <div className="d-flex gap-3">
            {(() => {
              const pending = memoizedAppointments.filter(a => a.status?.toLowerCase() === 'pending').length;
              const completed = memoizedAppointments.filter(a => a.status?.toLowerCase() === 'completed').length;
              const urgent = memoizedAppointments.filter(a => a.is_urgent || a.priority?.toLowerCase() === 'urgent').length;
              
              return (
                <>
                  {pending > 0 && (
                    <small className="text-muted">
                      <span className="badge bg-warning me-1">{pending}</span>
                      Pending
                    </small>
                  )}
                  {completed > 0 && (
                    <small className="text-muted">
                      <span className="badge bg-success me-1">{completed}</span>
                      Completed
                    </small>
                  )}
                  {urgent > 0 && (
                    <small className="text-muted">
                      <span className="badge bg-danger me-1">{urgent}</span>
                      Urgent
                    </small>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
      {/* Offcanvas para detalles de la cita */}
{selectedAppointment && (
  <div 
    className={`offcanvas offcanvas-end ${showOffcanvas ? 'show' : ''}`} 
    tabIndex="-1" 
    style={{ 
      visibility: showOffcanvas ? 'visible' : 'hidden',
      width: '400px'
    }}
  >
    <div className="offcanvas-header border-bottom">
      <h5 className="offcanvas-title d-flex align-items-center gap-2">
        <FiCalendar className="text-primary" />
        Appointment Details
      </h5>
      <button 
        type="button" 
        className="btn-close" 
        onClick={handleCloseOffcanvas}
        aria-label="Close"
      ></button>
    </div>
    
    <div className="offcanvas-body">
      <div className="row g-3">
        {/* Appointment ID */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body py-2">
              <small className="text-muted">Appointment ID</small>
              <div className="fw-bold text-primary">
                {selectedAppointment.appointmentId || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Status and Priority */}
        <div className="col-6">
          <small className="text-muted d-block mb-1">Status</small>
          <div className="d-flex align-items-center gap-1">
            <span style={{ color: getStatusIndicator(selectedAppointment.status).color, fontSize: '16px' }}>
              {getStatusIndicator(selectedAppointment.status).dot}
            </span>
            <span className="fw-medium">
              {selectedAppointment.status || 'Unknown'}
            </span>
          </div>
        </div>
        
        <div className="col-6">
          <small className="text-muted d-block mb-1">Priority</small>
          <div className="d-flex align-items-center gap-1">
            <span style={{ color: getPriorityIndicator(selectedAppointment.priority, selectedAppointment.is_urgent).color }}>
              {getPriorityIndicator(selectedAppointment.priority, selectedAppointment.is_urgent).icon}
            </span>
            <span className="fw-medium">
              {getPriorityIndicator(selectedAppointment.priority, selectedAppointment.is_urgent).text}
            </span>
          </div>
        </div>

        {/* Patient Information */}
        <div className="col-12">
          <div className="border-bottom pb-2 mb-3">
            <h6 className="mb-2 d-flex align-items-center gap-2">
              <FiUser className="text-primary" />
              Patient Information
            </h6>
          </div>
          
          <div className="mb-2">
            <small className="text-muted d-block">Patient Name</small>
            <div className="fw-medium">{selectedAppointment.patient_name || 'Unknown'}</div>
          </div>
          
          {selectedAppointment.date_of_birth && (
            <div className="mb-2">
              <small className="text-muted d-block">Date of Birth</small>
              <div>{formatDate(selectedAppointment.date_of_birth)}</div>
            </div>
          )}
          
          {selectedAppointment.address && (
            <div className="mb-2">
              <small className="text-muted d-block">Address</small>
              <div className="small">{selectedAppointment.address}</div>
            </div>
          )}
        </div>

        {/* Doctor Information */}
        <div className="col-12">
          <div className="border-bottom pb-2 mb-3">
            <h6 className="mb-2 d-flex align-items-center gap-2">
              <FiUser className="text-success" />
              Doctor Information
            </h6>
          </div>
          
          <div className="mb-2">
            <small className="text-muted d-block">Doctor</small>
            <div className="fw-medium">{selectedAppointment.doctor_name || 'Not assigned'}</div>
          </div>
          
          <div className="mb-2">
            <small className="text-muted d-block">Specialization</small>
            <div>{selectedAppointment.doctor_specialization || 'No specialty'}</div>
          </div>
        </div>

        {/* Appointment Details */}
        <div className="col-12">
          <div className="border-bottom pb-2 mb-3">
            <h6 className="mb-2 d-flex align-items-center gap-2">
              <FiCalendar className="text-info" />
              Appointment Details
            </h6>
          </div>
          
          <div className="row g-2">
            <div className="col-6">
              <small className="text-muted d-block">Date</small>
              <div className="fw-medium">{formatDate(selectedAppointment.appointment_date)}</div>
            </div>
            
            <div className="col-6">
              <small className="text-muted d-block">Time</small>
              <div className="fw-medium">
                {selectedAppointment.appointment_time}
                {selectedAppointment.end_time && ` - ${selectedAppointment.end_time}`}
              </div>
            </div>
            
            <div className="col-6">
              <small className="text-muted d-block">Type</small>
              <span className="badge bg-info">
                {selectedAppointment.appointment_type || 'Not specified'}
              </span>
            </div>
            
            <div className="col-6">
              <small className="text-muted d-block">Duration</small>
              <div>{selectedAppointment.duration || 30} minutes</div>
            </div>
            
            {selectedAppointment.room_number && (
              <div className="col-6">
                <small className="text-muted d-block">Room</small>
                <span className="badge bg-secondary">{selectedAppointment.room_number}</span>
              </div>
            )}
          </div>
        </div>

        {/* Reason for Visit */}
        {selectedAppointment.reason_for_visit && (
          <div className="col-12">
            <small className="text-muted d-block mb-1">Reason for Visit</small>
            <div className="p-2 bg-light rounded">
              {selectedAppointment.reason_for_visit}
            </div>
          </div>
        )}

        {/* Consultation Status */}
        <div className="col-12">
          <div className="border-bottom pb-2 mb-3">
            <h6 className="mb-2 d-flex align-items-center gap-2">
              <FiClock className="text-warning" />
              Consultation Status
            </h6>
          </div>
          
          <div className="row g-2">
            <div className="col-6">
              <small className="text-muted d-block">Check-in</small>
              <span className={`badge ${selectedAppointment.checked_in ? 'bg-success' : 'bg-secondary'}`}>
                {selectedAppointment.checked_in ? 'Checked In' : 'Not Checked In'}
              </span>
            </div>
            
            <div className="col-6">
              <small className="text-muted d-block">Consultation</small>
              <span className={`badge ${
                selectedAppointment.consultation_completed ? 'bg-success' : 
                selectedAppointment.consultation_started ? 'bg-primary' : 'bg-secondary'
              }`}>
                {selectedAppointment.consultation_completed ? 'Completed' : 
                 selectedAppointment.consultation_started ? 'In Progress' : 'Not Started'}
              </span>
            </div>
          </div>
          
          {selectedAppointment.wait_time > 0 && (
            <div className="mt-2">
              <small className="text-muted d-block">Wait Time</small>
              <div>{selectedAppointment.wait_time} minutes</div>
            </div>
          )}
        </div>

        {/* Additional Information */}
        {(selectedAppointment.is_walk_in || selectedAppointment.rescheduled_count > 0) && (
          <div className="col-12">
            <div className="border-bottom pb-2 mb-3">
              <h6 className="mb-2 d-flex align-items-center gap-2">
                <FiInfo className="text-secondary" />
                Additional Information
              </h6>
            </div>
            
            {selectedAppointment.is_walk_in && (
              <div className="mb-2">
                <span className="badge bg-warning">Walk-in Appointment</span>
                {selectedAppointment.walk_in_time && (
                  <div className="small text-muted mt-1">
                    Walk-in time: {selectedAppointment.walk_in_time}
                  </div>
                )}
              </div>
            )}
            
            {selectedAppointment.rescheduled_count > 0 && (
              <div className="mb-2">
                <small className="text-muted d-block">Rescheduled</small>
                <div>{selectedAppointment.rescheduled_count} time(s)</div>
              </div>
            )}
          </div>
        )}

        {/* Timestamps */}
        <div className="col-12">
          <div className="border-bottom pb-2 mb-3">
            <h6 className="mb-2 d-flex align-items-center gap-2">
              <FiClock className="text-muted" />
              Timeline
            </h6>
          </div>
          
          {selectedAppointment.created_date && (
            <div className="mb-2">
              <small className="text-muted d-block">Created</small>
              <div className="small">{formatDate(selectedAppointment.created_date)}</div>
            </div>
          )}
          
          {selectedAppointment.confirmed_at && (
            <div className="mb-2">
              <small className="text-muted d-block">Confirmed</small>
              <div className="small">{formatDate(selectedAppointment.confirmed_at)}</div>
            </div>
          )}
          
          {selectedAppointment.cancelled_at && (
            <div className="mb-2">
              <small className="text-muted d-block">Cancelled</small>
              <div className="small">{formatDate(selectedAppointment.cancelled_at)}</div>
              {selectedAppointment.cancellation_reason && (
                <div className="small text-danger mt-1">
                  Reason: {selectedAppointment.cancellation_reason}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>   
    
  </div>
)}
{/* Backdrop */}
{showOffcanvas && (
  <div 
    className="offcanvas-backdrop fade show" 
    onClick={handleCloseOffcanvas}
  ></div>
)}
    </div>
  );
};

export default AppointmentTreatmentTable;